/**
 * EXCEL-IMPORT (QA15-S B9): ressurser (spillested og festival) og personer (spillestedets folk og festivalens
 * stab).
 *
 * Tre steg, og bare det siste skriver: last ned en mal laget av organisasjonens egne grupper, avdelinger,
 * typer og poster; last opp, og få en vurdering per rad (ny / finnes fra før / feil, med forklaring); legg til.
 * Importen LEGGER BARE TIL. Den endrer eller fjerner aldri noe som finnes.
 *
 * Kolonnene kjennes igjen på overskriften, ikke på rekkefølgen, og en kolonne malen ikke kjenner, blir en
 * egenskap på ressursen (modell, serienummer) eller står igjen som «ikke brukt» for personer.
 */
import { z } from 'zod';

export const IMPORT_KINDS = ['venue-resources', 'resources', 'venue-people', 'people'] as const;
export const importKindSchema = z.enum(IMPORT_KINDS);
export type ImportKind = z.infer<typeof importKindSchema>;

/** Hvor mange rader en fil kan ha. Et hus med flere ting enn dette har et lager, ikke et regneark. */
export const MAX_IMPORT_ROWS = 2000;

export const importScopeQuerySchema = z
  .object({
    /** Spillestedet eller festivalen. */
    festivalId: z.uuid(),
    /** Festivalens årgang (avdelingene og ressursmerkingen er årgangens). Spillestedet har ingen. */
    editionId: z.uuid().optional(),
  })
  .meta({ id: 'ImportScopeQuery' });
export type ImportScopeQuery = z.infer<typeof importScopeQuerySchema>;

export const IMPORT_ROW_STATUSES = ['new', 'exists', 'error'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

/**
 * Hvorfor en rad står som den står. Koden er maskinlesbar og blir `imports.message.<camelCase>` i
 * nettleseren; `column` og `value` sier hvor.
 */
export const importMessageSchema = z.object({
  code: z.enum([
    'name_missing',
    'exists',
    'duplicate_in_file',
    'count_not_a_number',
    'count_on_single',
    'count_needed',
    'email_invalid',
    'type_created',
    'group_created',
    'post_created',
    'department_created',
    'has_login',
    'is_member',
    'matched_by_email',
    'matched_by_phone',
    'already_in_crew',
    /* QA16 (Del 4): «Totalt», «Sum» under tabellen er en summering, ikke en ting. */
    'sum_row',
  ]),
  column: z.string().nullable(),
  value: z.string().nullable(),
});
export type ImportMessage = z.infer<typeof importMessageSchema>;

const property = z.object({ label: z.string().trim().min(1).max(60), value: z.string().trim().max(500) });

/** En ressursrad slik den er lest, renset og klar til å legges til. */
export const resourceImportRowSchema = z.object({
  name: z.string().trim().min(1).max(120),
  type: z.string().trim().max(80).nullable(),
  count: z.number().int().min(1).max(100_000).nullable(),
  /** Gruppen (spillested) eller avdelingen (festival) som en vei: «Lyd › Mikrofoner». */
  group: z.array(z.string().trim().min(1).max(80)).max(10),
  notes: z.string().trim().max(2000).nullable(),
  properties: z.array(property).max(20),
});
export type ResourceImportRow = z.infer<typeof resourceImportRowSchema>;

/** En personrad slik den er lest. */
export const personImportRowSchema = z.object({
  name: z.string().trim().min(1).max(160),
  email: z.string().trim().max(255).nullable(),
  phone: z.string().trim().max(40).nullable(),
  /** Spillestedet: posten (Lydtekniker). Festivalen: rollen (fritekst). */
  post: z.string().trim().max(120).nullable(),
  department: z.string().trim().max(80).nullable(),
  note: z.string().trim().max(500).nullable(),
});
export type PersonImportRow = z.infer<typeof personImportRowSchema>;

export const importPreviewRowSchema = z.object({
  /** Raden i regnearket, som brukeren ser den (1 er første rad). */
  row: z.number().int(),
  status: z.enum(IMPORT_ROW_STATUSES),
  /** Navnet slik det står i fila, også når raden har feil. */
  label: z.string(),
  resource: resourceImportRowSchema.nullable(),
  person: personImportRowSchema.nullable(),
  messages: z.array(importMessageSchema),
});
export type ImportPreviewRow = z.infer<typeof importPreviewRowSchema>;

export const importPreviewSchema = z
  .object({
    kind: importKindSchema,
    rows: z.array(importPreviewRowSchema),
    counts: z.object({
      new: z.number().int(),
      exists: z.number().int(),
      error: z.number().int(),
    }),
    /** Kolonnene som ble kjent igjen (navnet i fila → hva det ble), og de som ikke ble brukt. */
    columns: z.array(z.object({ header: z.string(), usedAs: z.string().nullable() })),
    /** Det som lages i tillegg: typer, grupper, poster og avdelinger som ikke fantes. */
    creates: z.array(z.object({ kind: z.enum(['type', 'group', 'post', 'department']), name: z.string() })),
    /** Arket som ble lest, når fila har flere. */
    sheet: z.string(),
  })
  .meta({ id: 'ImportPreview' });
export type ImportPreview = z.infer<typeof importPreviewSchema>;

export const importCommitRequestSchema = z
  .object({
    resources: z.array(resourceImportRowSchema).max(MAX_IMPORT_ROWS).optional(),
    people: z.array(personImportRowSchema).max(MAX_IMPORT_ROWS).optional(),
  })
  .meta({ id: 'ImportCommitRequest' });
export type ImportCommitRequest = z.infer<typeof importCommitRequestSchema>;

export const importCommitResultSchema = z
  .object({
    added: z.number().int(),
    /** Rader som fantes da de skulle legges til (noen la dem inn mens forhåndsvisningen sto åpen). */
    skipped: z.number().int(),
  })
  .meta({ id: 'ImportCommitResult' });
export type ImportCommitResult = z.infer<typeof importCommitResultSchema>;
